import { useState, useEffect } from 'react';
import { Star, Quote } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axios.get(`${API}/reviews`);
        setReviews(response.data);
      } catch (error) {
        console.error('Failed to fetch reviews:', error);
        // Fallback reviews if API fails
        setReviews([
          {
            id: '1',
            name: 'Priya Sharma',
            rating: 5,
            text: 'Excellent treatment for my chronic back pain. The Panchakarma therapy worked wonders.',
            date: '2 weeks ago',
            avatar_initial: 'P'
          },
          {
            id: '2',
            name: 'Rajesh Kumar',
            rating: 5,
            text: 'Been suffering from digestive issues for years. After just 3 weeks of treatment, I feel amazing.',
            date: '1 month ago',
            avatar_initial: 'R'
          },
          {
            id: '3',
            name: 'Anjali Patel',
            rating: 5,
            text: 'Amazing experience! The doctors really take time to understand your health concerns.',
            date: '3 weeks ago',
            avatar_initial: 'A'
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  const renderStars = (rating) => {
    return Array(5)
      .fill(0)
      .map((_, i) => (
        <Star
          key={i}
          className={`w-4 h-4 ${
            i < rating ? 'fill-[#C67D63] text-[#C67D63]' : 'text-gray-300'
          }`}
        />
      ));
  };

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="section-padding bg-[#2D4A3E]"
    >
      <div className="section-container">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-left mb-16 max-w-2xl"
        >
          <span className="text-sm tracking-widest uppercase font-semibold text-[#C67D63] mb-4 block">
            Testimonials
          </span>
          <h2
            className="heading-2 text-white mb-6"
            data-testid="reviews-heading"
          >
            Happy Customers
          </h2>
          <p className="body-large text-white/70">
            Don't just take our word for it. Here's what our patients say about
            their healing journey with AyurvedMed Healthcare.
          </p>
        </motion.div>

        {/* Reviews Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 animate-pulse"
              >
                <div className="h-4 bg-white/20 rounded w-24 mb-4" />
                <div className="h-20 bg-white/20 rounded mb-4" />
                <div className="h-4 bg-white/20 rounded w-32" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((review, index) => (
              <motion.div
                key={review.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-white/20 transition-colors duration-300"
                data-testid={`review-card-${review.id}`}
              >
                {/* Quote Icon */}
                <Quote className="w-8 h-8 text-[#C67D63]/50 mb-4" />

                {/* Stars */}
                <div className="flex gap-1 mb-4">{renderStars(review.rating)}</div>

                {/* Review Text */}
                <p className="text-white/90 leading-relaxed mb-6">
                  "{review.text}"
                </p>

                {/* Reviewer Info */}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#C67D63] flex items-center justify-center text-white font-semibold">
                    {review.avatar_initial}
                  </div>
                  <div>
                    <p className="text-white font-medium">{review.name}</p>
                    <p className="text-white/50 text-sm">{review.date}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Google Rating Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 flex items-center justify-center gap-4 bg-white/10 backdrop-blur-sm rounded-full px-8 py-4 w-fit mx-auto"
        >
          <div className="flex items-center gap-1">
            {renderStars(4)}
            <Star className="w-4 h-4 fill-[#C67D63]/50 text-[#C67D63]/50" />
          </div>
          <span className="text-white font-semibold">4.4</span>
          <span className="text-white/70">Google Reviews Rating</span>
        </motion.div>
      </div>
    </section>
  );
};

export default Reviews;
